// Top-level switch between the two chat "layers": datatalk (DataChatPanel, mode is
// "monitoring"/"prediction") and RAG guideline chat (LeitlinienChatPanel, mode is
// "leitlinien"). Owns the RAG-only settings popover (document selection, similarity
// threshold, top_k) since only LeitlinienChatPanel needs those controls.
import { useState } from "react"
import { ContextOption, ValueType } from "pokal_api"
import DataChatPanel from "../data/DataChatPanel"
import LeitlinienChatPanel from "../rag/LeitlinienChatPanel"
import DocumentSelectorDropdown from "../rag/DocumentSelectorDropdown"
import UploadDocumentModal from "../rag/UploadDocumentModal"
import { valueTypeShortNames } from "../../../../util/valueTypeNames"

type Props = {
    patientId: string
    mode: "monitoring" | "prediction" | "leitlinien"
    // which page this panel lives on - fixed per caller, used to keep the two pages'
    // Leitlinien chat histories separate regardless of the "leitlinien" sub-tab toggle above
    pageContext: "monitoring" | "prediction"
    onClose?: () => void
    selectedDocumentIds?: string[]
    onSelectionChange?: (ids: string[]) => void
    similarityThreshold?: number
    onThresholdChange?: (v: number) => void
    topK?: number
    onTopKChange?: (v: number) => void
    contextSelections?: Partial<Record<ValueType, ContextOption[]>>
    predictionContextEnabled?: boolean
}

export default function LayerChatPanel({
    patientId,
    mode,
    pageContext,
    onClose,
    selectedDocumentIds = [],
    onSelectionChange,
    similarityThreshold = 0.7,
    onThresholdChange,
    topK = 5,
    onTopKChange,
    contextSelections,
    predictionContextEnabled = false,
}: Props) {
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [uploadOpen, setUploadOpen] = useState(false)

    const activeBadges: { key: string; label: string }[] = contextSelections
        ? Object.entries(contextSelections).flatMap(([vt, opts]) =>
              (opts ?? []).map(opt => ({
                  key: `${vt}-${opt}`,
                  label: `${valueTypeShortNames[vt as unknown as ValueType] ?? vt} ${opt === "14days" ? "14T" : "Trend"}`,
              }))
          )
        : []

    return (
        <div className="flex flex-col h-full bg-teal-50 border border-teal-400 rounded-xl overflow-hidden">
            <div className="px-3 py-2 border-b border-teal-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-900">Analyse</span>
                    <div className="flex items-center gap-2 relative">
                        {mode === "leitlinien" && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setSettingsOpen(v => !v)}
                                    className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs text-gray-700 hover:border-gray-400"
                                >
                                    ⚙ Einstellungen ▾
                                </button>
                                {settingsOpen && (
                                    <DocumentSelectorDropdown
                                        selectedDocumentIds={selectedDocumentIds}
                                        onSelectionChange={ids => { onSelectionChange?.(ids) }}
                                        similarityThreshold={similarityThreshold}
                                        onThresholdChange={v => onThresholdChange?.(v)}
                                        topK={topK}
                                        onTopKChange={v => onTopKChange?.(v)}
                                        onUploadClick={() => { setSettingsOpen(false); setUploadOpen(true) }}
                                        onClose={() => setSettingsOpen(false)}
                                    />
                                )}
                                {uploadOpen && (
                                    <UploadDocumentModal
                                        onSuccess={() => setUploadOpen(false)}
                                        onClose={() => setUploadOpen(false)}
                                    />
                                )}
                            </>
                        )}
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Schließen"
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded text-base leading-none"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {activeBadges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {activeBadges.map(({ key, label }) => (
                            <span
                                key={key}
                                className="text-xs bg-teal-100 text-teal-700 border border-teal-300 rounded-full px-2 py-0.5"
                            >
                                {label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {mode === "leitlinien" ? (
                <LeitlinienChatPanel
                    patientId={patientId}
                    pageContext={pageContext}
                    selectedDocumentIds={selectedDocumentIds}
                    similarityThreshold={similarityThreshold}
                    topK={topK}
                    predictionContextEnabled={predictionContextEnabled}
                    contextSelections={contextSelections}
                />
            ) : (
                <DataChatPanel
                    patientId={patientId}
                    mode={mode}
                    contextSelections={contextSelections}
                />
            )}
        </div>
    )
}
